import {
  EMOTE_ETICHETTA_MAX,
  EMOTE_MAX_POSTI,
  type MyEmoteCandidate,
  type MyEmoteSlot,
  type PutMyEmotesBody,
} from '@/services/avatarApi';
import { EMOJI_TAVOLO } from '@/lib/qgammon/tavolo';

/**
 * «Le mie emote» (I-581, ADR-0218): la parte pura della schermata di scelta, senza React.
 *
 * La BOZZA e' cio' che il giocatore sta componendo: fino a 6 clip, in ordine (l'ordine e' lo slot), ciascuna con l'emoji di
 * ripiego (uno dei 12 codici del tavolo) e un'etichetta opzionale. L'etichetta e' SOLO del proprietario: non va a nessuno.
 */
export interface PostoBozza {
  clipId: string;
  fallback: string;
  label: string;
}

export type Bozza = readonly PostoBozza[];

const CODICI: readonly string[] = EMOJI_TAVOLO.map(([codice]) => codice);

/** Il set salvato, in ordine di slot, con un codice di ripiego che questa build conosce (altrimenti il primo). */
export function bozzaDaServer(slots: readonly MyEmoteSlot[]): Bozza {
  return [...slots]
    .sort((a, b) => a.slot - b.slot)
    .slice(0, EMOTE_MAX_POSTI)
    .map((s) => ({
      clipId: s.clipId,
      fallback: CODICI.includes(s.fallback) ? s.fallback : CODICI[0],
      label: (s.label ?? '').slice(0, EMOTE_ETICHETTA_MAX),
    }));
}

/** Il primo codice non ancora usato da un altro posto (cosi' due posti nuovi non partono con lo stesso ripiego), o il primo. */
function ripiegoLibero(bozza: Bozza): string {
  return CODICI.find((c) => !bozza.some((p) => p.fallback === c)) ?? CODICI[0];
}

/**
 * Aggiunge la clip se manca, la toglie se c'e'. Si aggiunge solo una clip PRONTA (versione «emote» pronta) e solo finche' ci sono
 * meno di 6 posti: in tutti gli altri casi la bozza resta com'e' (la stessa istanza).
 */
export function alternaClip(bozza: Bozza, clipId: string, candidate: readonly MyEmoteCandidate[]): Bozza {
  if (bozza.some((p) => p.clipId === clipId)) return bozza.filter((p) => p.clipId !== clipId);
  const c = candidate.find((x) => x.clipId === clipId);
  if (!c || c.emoteStatus !== 'ready' || bozza.length >= EMOTE_MAX_POSTI) return bozza;
  return [...bozza, { clipId, fallback: ripiegoLibero(bozza), label: (c.label ?? '').slice(0, EMOTE_ETICHETTA_MAX) }];
}

export function cambiaPosto(bozza: Bozza, clipId: string, cambio: Partial<Pick<PostoBozza, 'fallback' | 'label'>>): Bozza {
  return bozza.map((p) => {
    if (p.clipId !== clipId) return p;
    const fallback = cambio.fallback !== undefined && CODICI.includes(cambio.fallback) ? cambio.fallback : p.fallback;
    const label = cambio.label !== undefined ? cambio.label.slice(0, EMOTE_ETICHETTA_MAX) : p.label;
    return { ...p, fallback, label };
  });
}

/** Sposta un posto di uno verso l'alto (`-1`) o il basso (`+1`); ai bordi non fa niente. */
export function spostaPosto(bozza: Bozza, clipId: string, verso: -1 | 1): Bozza {
  const i = bozza.findIndex((p) => p.clipId === clipId);
  const j = i + verso;
  if (i < 0 || j < 0 || j >= bozza.length) return bozza;
  const copia = [...bozza];
  [copia[i], copia[j]] = [copia[j], copia[i]];
  return copia;
}

export function bozzeUguali(a: Bozza, b: Bozza): boolean {
  return a.length === b.length && a.every((p, i) => p.clipId === b[i].clipId && p.fallback === b[i].fallback && p.label === b[i].label);
}

/** Il corpo del `PUT`: la `rev` LETTA, le etichette ripulite (una vuota non si manda). */
export function corpoPut(bozza: Bozza, rev: number): PutMyEmotesBody {
  return {
    rev,
    slots: bozza.map((p) => {
      const label = p.label.trim();
      return label ? { clipId: p.clipId, fallback: p.fallback, label } : { clipId: p.clipId, fallback: p.fallback };
    }),
  };
}

/**
 * Il codice di errore di Qavatar (`{ success: false, error: "<codice>" }`), o `undefined`: `rev_conflict` (409),
 * `featured_not_valid` · `clip_not_candidate` · `clip_not_ready` (422), `too_many_slots` · `bad_fallback` · `bad_label` ·
 * `duplicate_clip` · `invalid_body` (400).
 */
export function codiceErrore(errore: unknown): string | undefined {
  const c = (errore as { data?: { error?: unknown } } | null | undefined)?.data?.error;
  return typeof c === 'string' ? c : undefined;
}

/** Lo stato HTTP di un errore di RTK Query (`fetchBaseQuery`), o `undefined`. */
export function statoHttp(errore: unknown): number | undefined {
  const s = (errore as { status?: unknown } | null | undefined)?.status;
  return typeof s === 'number' ? s : undefined;
}
